import { SxProps, Theme } from '@mui/material/styles';

// Common field animations and styles
export const fieldAnimationStyles: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '2px',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '2px',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)',
      },
    },
    '&.Mui-error': {
      animation: 'shake 0.5s ease-in-out',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'error.main',
      },
    },
  },
  '& .MuiInputLabel-root': {
    transition: 'all 0.2s ease-in-out',
    '&.Mui-focused': {
      color: 'primary.main',
      fontWeight: 600,
    },
  },
};

// Pulse animation for required fields that are empty
export const requiredFieldPulseStyles: SxProps<Theme> = {
  '&.required-empty': {
    animation: 'pulse 2s ease-in-out infinite',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'warning.main',
      borderWidth: '2px',
    },
    '& .MuiInputLabel-root': {
      color: 'warning.main',
    },
  },
};

// Button hover animations
export const buttonAnimationStyles: SxProps<Theme> = {
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
};

// Building list item animations
export const buildingListAnimationStyles: SxProps<Theme> = {
  transition: 'all 0.3s ease-in-out',
  '&.building-enter': {
    opacity: 0,
    transform: 'translateX(-20px) scale(0.95)',
  },
  '&.building-enter-active': {
    opacity: 1,
    transform: 'translateX(0) scale(1)',
  },
  '&.building-exit': {
    opacity: 1,
    transform: 'translateX(0) scale(1)',
  },
  '&.building-exit-active': {
    opacity: 0,
    transform: 'translateX(20px) scale(0.95)',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
};

// Notes animation styles
export const notesAnimationStyles: SxProps<Theme> = {
  '& .note-item': {
    transition: 'all 0.3s ease-in-out',
    '&.note-enter': {
      opacity: 0,
      transform: 'translateY(-10px) scale(0.95)',
    },
    '&.note-enter-active': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
    '&.note-exit': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
    '&.note-exit-active': {
      opacity: 0,
      transform: 'translateY(-10px) scale(0.95)',
    },
  },
};

// CSS keyframes (to be added to a global CSS file or styled component)
export const globalAnimationKeyframes = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// Section reveal animation styles
export const sectionRevealStyles: SxProps<Theme> = {
  animation: 'fadeInUp 0.6s ease-out',
  '&.delayed-1': {
    animationDelay: '0.1s',
    animationFillMode: 'both',
  },
  '&.delayed-2': {
    animationDelay: '0.2s',
    animationFillMode: 'both',
  },
  '&.delayed-3': {
    animationDelay: '0.3s',
    animationFillMode: 'both',
  },
};

// Accordion/Expandable section styles
export const accordionAnimationStyles: SxProps<Theme> = {
  '& .MuiAccordion-root': {
    transition: 'all 0.3s ease-in-out',
    '&:before': {
      transition: 'opacity 0.3s ease-in-out',
    },
    '&.Mui-expanded': {
      '&:before': {
        opacity: 0,
      },
    },
  },
  '& .MuiAccordionSummary-root': {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  '& .MuiAccordionDetails-root': {
    animation: 'fadeInUp 0.3s ease-out',
  },
};

// Helper function to determine if field should pulse (required but empty)
export const shouldPulse = (value: string | number | null | undefined, required: boolean = false): boolean => {
  if (!required) return false;

  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (typeof value === 'number') {
    return value === 0 || isNaN(value);
  }

  return value == null;
};

// Helper functions to return styles as spreadable objects
export const getFieldAnimationStyles = () => ({ ...fieldAnimationStyles });
export const getRequiredFieldPulseStyles = () => ({ ...requiredFieldPulseStyles });
